import React, { useEffect, useRef, useState } from 'react';
import { Box, CircularProgress, IconButton, Tooltip, Typography } from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import UndoIcon from '@mui/icons-material/Undo';
import { Link } from 'react-router-dom';

type SaveStatus = 'idle' | 'editing' | 'saving';

const LOCKED_BG = '#fff';
const LOCKED_FG = 'text.primary';
const SAVING_COLOR = '#21e77a';
const SAVING_BADGE_BG = SAVING_COLOR;
const SAVING_BADGE_FG = '#0b2a1a';
const SELF_EDIT_BADGE_BG = SAVING_BADGE_BG;
const SELF_EDIT_BADGE_FG = SAVING_BADGE_FG;
const BODY_TEXT_COLOR = 'rgb(24, 5, 0)';

// Inline field status (editing/saving) shown near title/body/feedback labels.
export const StatusIndicator: React.FC<{ status: SaveStatus; locked?: boolean }> = ({
  status,
  locked = false,
}) => {
  const display = (label: string, color: 'primary.main' | 'text.secondary') => (
    <Typography
      variant="caption"
      color={color}
      sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}
    >
      {label}
    </Typography>
  );
  switch (status) {
    case 'editing':
      return display('編集中…', 'primary.main');
    case 'saving':
      if (locked) return null;
      return (
        <Box sx={{ display: 'inline-flex', alignItems: 'center' }}>
          <CircularProgress size={16} sx={{ ml: 0.5 }} />
          {display('保存中…', 'text.secondary')}
        </Box>
      );
    default:
      return null;
  }
};

export const SelfEditingBadge: React.FC<{ active: boolean; label?: string }> = ({ active, label }) => {
  if (!active) return null;
  const displayLabel = label ?? '自分が編集中';
  return (
    <Box
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 0.5,
        px: 1,
        py: 0.25,
        borderRadius: 999,
        backgroundColor: SELF_EDIT_BADGE_BG,
        color: SELF_EDIT_BADGE_FG,
        fontSize: 11,
        fontWeight: 700,
        whiteSpace: 'nowrap',
      }}
    >
      <Box sx={{ width: 6, height: 6, borderRadius: '50%', backgroundColor: SELF_EDIT_BADGE_FG }} />
      {displayLabel}
    </Box>
  );
};

export const RowSavingBadge: React.FC<{ active: boolean }> = ({ active }) => {
  if (!active) return null;
  return (
    <Box
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 0.5,
        px: 1,
        py: 0.25,
        borderRadius: 999,
        backgroundColor: SAVING_BADGE_BG,
        color: SAVING_BADGE_FG,
        fontSize: 14,
        fontWeight: 700,
        whiteSpace: 'nowrap',
      }}
    >
      <CircularProgress size={14} sx={{ color: SAVING_COLOR }} />
      保存中…
    </Box>
  );
};

const TOOLTIP_SPAN_STYLE: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  lineHeight: 1,
  padding: 0,
  margin: 0,
};

// Right-end actions for title/body/feedback headers.
export const FieldEndActions: React.FC<{
  label: string;
  locked: boolean;
  saving?: boolean;
  synced: boolean;
  canRollback: boolean;
  onRollback: () => void;
}> = ({ label, locked, saving = false, synced, canRollback, onRollback }) => {
  const disabled = locked || saving;
  const showSynced = !disabled && synced;

  return (
    <Box
      sx={{
        height: '100%',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'flex-end',
        flexWrap: 'nowrap',
        whiteSpace: 'nowrap',
        gap: 0.5,
        p: 0,
        m: 0,
        lineHeight: 1,
        border: 'none',
        backgroundColor: 'transparent',
        '& svg': { display: 'block' },
      }}
      onMouseDown={(e) => {
        e.preventDefault();
        e.stopPropagation();
      }}
      onClick={(e) => {
        e.stopPropagation();
      }}
    >
      <Typography
        component="span"
        variant="body2"
        sx={{
          fontSize: '12px',
          fontWeight: 700,
          color: disabled ? 'text.disabled' : 'text.secondary',
          lineHeight: 1,
          m: 0,
          p: 0,
          display: 'inline-flex',
          alignItems: 'center',
        }}
      >
        {label}
      </Typography>

      {saving ? (
        <CircularProgress size={14} sx={{ display: 'block' }} />
      ) : showSynced ? (
        <CheckCircleIcon sx={{ fontSize: 16, display: 'block' }} color="success" />
      ) : (
        <CheckCircleOutlineIcon sx={{ fontSize: 16, display: 'block' }} color="disabled" />
      )}

      <Tooltip title="変更を元に戻します" arrow>
        <span style={TOOLTIP_SPAN_STYLE}>
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              onRollback();
            }}
            disabled={disabled || !canRollback}
            sx={{
              p: 0,
              m: 0,
              mt: '0 !important',
              width: 20,
              height: 20,
              mr: 0.5,
              minWidth: 0,
              minHeight: 0,
              lineHeight: 1,
              color: !disabled && canRollback ? 'primary.main' : 'action.disabled',
              '& .MuiSvgIcon-root': { display: 'block' },
            }}
          >
            <UndoIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </span>
      </Tooltip>
    </Box>
  );
};

export const LinkifyDisplay: React.FC<{ text: string }> = ({ text }) => {
  const urlRegex = /https?:\/\/\S+/g;
  const elements: React.ReactNode[] = [];
  let lastIndex = 0;
  let match: RegExpExecArray | null;

  while ((match = urlRegex.exec(text)) !== null) {
    const url = match[0];
    const idx = match.index;
    elements.push(text.slice(lastIndex, idx));
    const relative = url.startsWith(window.location.origin)
      ? url.replace(window.location.origin, '')
      : url;
    elements.push(
      <Link
        key={idx}
        to={relative}
        target="_blank"
        rel="noopener noreferrer"
        onClick={(e) => e.stopPropagation()}
      >
        {url}
      </Link>
    );
    lastIndex = idx + url.length;
  }
  elements.push(text.slice(lastIndex));
  return <>{elements}</>;
};

// Convert pointer coordinates to caret index within a display node.
export const getCaretIndexFromPoint = (root: HTMLElement, clientX: number, clientY: number): number => {
  const doc = root.ownerDocument as any;

  let range: Range | null = null;
  if (typeof doc.caretPositionFromPoint === 'function') {
    const pos = doc.caretPositionFromPoint(clientX, clientY);
    if (pos && pos.offsetNode) {
      range = root.ownerDocument.createRange();
      range.setStart(pos.offsetNode, pos.offset ?? 0);
      range.collapse(true);
    }
  }

  if (!range && typeof doc.caretRangeFromPoint === 'function') {
    range = doc.caretRangeFromPoint(clientX, clientY) as Range | null;
  }

  if (!range) {
    return (root.textContent ?? '').length;
  }

  if (!root.contains(range.startContainer)) {
    return (root.textContent ?? '').length;
  }

  const pre = root.ownerDocument.createRange();
  pre.selectNodeContents(root);
  pre.setEnd(range.startContainer, range.startOffset);
  return pre.toString().length;
};

// Detect browser translation mode (used to switch to display divs for stable UX).
export const useIsBrowserTranslated = (): boolean => {
  const [translated, setTranslated] = useState(false);

  useEffect(() => {
    const html = document.documentElement;

    const detect = () => {
      const cls = String(html?.className ?? '');
      const hasTranslatedClass = /\btranslated-/.test(cls);
      const hasGoogleTranslateNodes = !!document.querySelector(
        'iframe.goog-te-banner-frame, .goog-te-banner-frame, .skiptranslate'
      );
      setTranslated(hasTranslatedClass || hasGoogleTranslateNodes);
    };

    detect();

    const moHtml = new MutationObserver(detect);
    try {
      moHtml.observe(html, { attributes: true, attributeFilter: ['class'] });
    } catch {}

    const moBody = new MutationObserver(detect);
    try {
      if (document.body) {
        moBody.observe(document.body, { childList: true, subtree: true });
      }
    } catch {}

    return () => {
      moHtml.disconnect();
      moBody.disconnect();
    };
  }, []);

  return translated;
};

// Display-only replacement for text input in translated mode.
export const TranslatableDisplayField: React.FC<{
  id?: string;
  className?: string;
  value: string;
  disabled?: boolean;
  placeholder?: string;
  onStartEdit?: (caretIndex: number) => void;
}> = ({ id, className = '', value, disabled = false, placeholder = '', onStartEdit }) => {
  const text = String(value ?? '');
  const isEmpty = text.trim() === '';

  const outerRef = useRef<HTMLDivElement | null>(null);
  const innerRef = useRef<HTMLDivElement | null>(null);

  const beginEdit = (e: React.SyntheticEvent, caretIndex?: number) => {
    if (disabled) return;
    e.preventDefault();
    e.stopPropagation();
    const fallback = isEmpty ? 0 : text.length;
    onStartEdit?.(typeof caretIndex === 'number' ? caretIndex : fallback);
  };

  return (
    <div
      ref={outerRef}
      id={id}
      className={className}
      tabIndex={disabled ? -1 : 0}
      role="textbox"
      aria-readonly="true"
      onMouseDown={(e) => {
        if (disabled) return;
        const container = innerRef.current ?? outerRef.current;
        const caretIndex =
          !container || isEmpty
            ? 0
            : getCaretIndexFromPoint(container, e.clientX, e.clientY);
        beginEdit(e, caretIndex);
      }}
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
      }}
      onKeyDown={(e) => {
        if (disabled) return;
        if (e.key === 'Enter' || e.key === ' ') beginEdit(e, text.length);
      }}
    >
      <div ref={innerRef} className="MuiInputBase-input">
        {isEmpty ? (
          <span className="wr-display-placeholder">{placeholder}</span>
        ) : (
          <LinkifyDisplay text={text} />
        )}
      </div>
    </div>
  );
};

// Display-only replacement for textarea in translated mode.
export const TranslatableDisplayTextarea: React.FC<{
  id?: string;
  className?: string;
  value: string;
  disabled?: boolean;
  placeholder?: string;
  onStartEdit?: (caretIndex: number) => void;
  minHeightPx?: number;
  paddingRightPx?: number;
}> = ({
  id,
  className = '',
  value,
  disabled = false,
  placeholder = '',
  onStartEdit,
  minHeightPx = 200,
  paddingRightPx = 56,
}) => {
  const text = String(value ?? '');
  const isEmpty = text.trim() === '';

  const outerRef = useRef<HTMLDivElement | null>(null);
  const innerRef = useRef<HTMLDivElement | null>(null);

  const beginEdit = (e: React.SyntheticEvent, caretIndex?: number) => {
    if (disabled) return;
    e.preventDefault();
    e.stopPropagation();
    const fallback = isEmpty ? 0 : text.length;
    onStartEdit?.(typeof caretIndex === 'number' ? caretIndex : fallback);
  };

  return (
    <Box
      ref={outerRef}
      id={id}
      className={className}
      tabIndex={disabled ? -1 : 0}
      role="textbox"
      aria-readonly="true"
      aria-multiline="true"
      onMouseDown={(e: React.MouseEvent) => {
        if (disabled) return;
        const target = e.target as HTMLElement | null;
        if (target?.closest('a')) return;
        const container = innerRef.current ?? outerRef.current;
        const caretIndex =
          !container || isEmpty
            ? 0
            : getCaretIndexFromPoint(container, e.clientX, e.clientY);
        beginEdit(e, caretIndex);
      }}
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
      }}
      onKeyDown={(e) => {
        if (disabled) return;
        if (e.key === 'Enter' || e.key === ' ') beginEdit(e, text.length);
      }}
      sx={{
        width: '100%',
        boxSizing: 'border-box',
        minHeight: `${minHeightPx}px`,
        border: '1px solid rgba(0,0,0,0.23)',
        borderRadius: 1,
        backgroundColor: disabled ? LOCKED_BG : '#fff',
        overflow: 'auto',
        outline: 'none',
        '&:focus': {
          borderColor: 'rgba(25, 118, 210, 0.8)',
          boxShadow: '0 0 0 2px rgba(25, 118, 210, 0.15)',
        },
      }}
    >
      <Box
        ref={innerRef}
        className="MuiInputBase-input"
        sx={{
          width: '100%',
          display: 'block',
          fontFamily: 'monospace',
          fontSize: 'inherit',
          lineHeight: 1,
          color: disabled ? LOCKED_FG : BODY_TEXT_COLOR,
          whiteSpace: 'pre-wrap',
          wordBreak: 'break-word',
          pr: `${paddingRightPx}px`,
          pt: '8px',
          pb: '8px',
          pl: '10px',
        }}
      >
        {isEmpty ? (
          <span className="wr-display-placeholder">{placeholder}</span>
        ) : (
          <LinkifyDisplay text={text} />
        )}
      </Box>
    </Box>
  );
};
